import { useState, useEffect, useCallback } from 'react';
import { api, type AdminAnalyticsOverview, type AnalyticsPeriod } from '@/lib/api';
import { PeriodSelector } from './components/PeriodSelector';
import { StatCard } from './components/StatCard';
import { TimeSeriesChart } from './components/TimeSeriesChart';
import { BreakdownTable } from './components/BreakdownTable';

export function OverviewTab() {
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AdminAnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(
    async (nocache = false) => {
      setLoading(true);
      setError(null);
      try {
        const result = await api.adminGetAnalyticsOverview(period, nocache);
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load overview');
      } finally {
        setLoading(false);
      }
    },
    [period],
  );

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-24 bg-muted rounded-lg" />
          ))}
        </div>
        <div className="h-64 bg-muted rounded-lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PeriodSelector
          period={period}
          onChange={setPeriod}
          onRefresh={() => fetchData(true)}
          loading={loading}
        />
      </div>

      {error && (
        <div className="mb-6 p-4 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm">
          {error}
        </div>
      )}

      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="DAU" value={data.dau} hint="active in last 24h" tone="positive" />
            <StatCard label="WAU" value={data.wau} hint="active in last 7 days" />
            <StatCard label="MAU" value={data.mau} hint="active in last 30 days" />
            <StatCard
              label="New Users"
              value={data.newUsersPeriod}
              hint={`first event in ${period}`}
            />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <StatCard label="Total Users" value={data.totalUsers} />
            <StatCard label="Active Subscribers" value={data.activeSubscribers} />
            <StatCard label="Period Spend" value={data.totalSpendDisplay} tone="warning" />
            <StatCard
              label="Avg / Day"
              value={`$${(data.totalSpendCredits / 100 / Math.max(data.dailySpendSeries.length, 1)).toFixed(2)}`}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Daily Active Users</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Users with at least one event per day. Heartbeat / page-view / feature events all
              count.
            </p>
            <TimeSeriesChart
              data={data.dailySeries}
              series={[
                { dataKey: 'activeUsers', label: 'Active users', color: '#22c55e' },
                { dataKey: 'sessions', label: 'Sessions', color: '#3b82f6' },
              ]}
            />
          </div>

          <div className="bg-card rounded-lg border border-border p-6 mb-6">
            <h2 className="text-lg font-semibold text-foreground mb-2">Daily Spend</h2>
            <p className="text-xs text-muted-foreground mb-4">
              Total credits spent per day across all paid features. 1 credit = $0.01.
            </p>
            <TimeSeriesChart
              data={data.dailySpendSeries}
              series={[{ dataKey: 'spendCredits', label: 'Credits', color: '#f59e0b' }]}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Top Pages</h2>
              <BreakdownTable
                rows={data.topPagesByViews}
                columns={[
                  { key: 'pageName', label: 'Page' },
                  { key: 'views', label: 'Views', align: 'right' },
                ]}
              />
            </div>
            <div className="bg-card rounded-lg border border-border p-6">
              <h2 className="text-lg font-semibold text-foreground mb-4">Top Arches by Opens</h2>
              <BreakdownTable
                rows={data.topArchesByOpens}
                columns={[
                  { key: 'archeId', label: 'Arche' },
                  { key: 'opens', label: 'Opens', align: 'right' },
                  { key: 'uniqueUsers', label: 'Users', align: 'right' },
                ]}
              />
            </div>
          </div>

          <div className="bg-card rounded-lg border border-border p-6">
            <h2 className="text-lg font-semibold text-foreground mb-4">Top Expense Categories</h2>
            <BreakdownTable
              rows={data.topInvocationCategories}
              columns={[
                { key: 'category', label: 'Category' },
                { key: 'invocations', label: 'Invocations', align: 'right' },
                {
                  key: 'totalCredits',
                  label: 'Spend',
                  align: 'right',
                  render: (r) => `$${(r.totalCredits / 100).toFixed(2)}`,
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}
